import { useState, useEffect, useCallback } from "react";
import { adminGetAuditLogs } from "@/api";
import {
  ScrollText, Search, Filter, ChevronLeft, ChevronRight,
  Calendar, User, FileText, Settings, CreditCard, UserCog,
  Clock, Info, Shield, RefreshCw, ChevronDown, ChevronUp
} from "lucide-react";

const ENTITY_CONFIG = {
  user: { label: "User", color: "bg-blue-100 text-blue-700", icon: User },
  application: { label: "Application", color: "bg-violet-100 text-violet-700", icon: FileText },
  disbursement: { label: "Disbursement", color: "bg-emerald-100 text-emerald-700", icon: CreditCard },
  agent: { label: "Agent", color: "bg-amber-100 text-amber-700", icon: UserCog },
  settings: { label: "Settings", color: "bg-gray-100 text-gray-700", icon: Settings },
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [entityType, setEntityType] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (entityType !== "all") params.entity_type = entityType;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await adminGetAuditLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      console.error("[AuditLogs] Error:", e);
    } finally {
      setLoading(false);
    }
  }, [page, entityType, search, dateFrom, dateTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setEntityType("all");
    setSearch("");
    setSearchInput("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters = entityType !== "all" || search || dateFrom || dateTo;

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Audit Logs
          </h2>
          <p className="text-sm text-[#6B7280] mt-1">Track all admin actions and system changes</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B7280] bg-gray-50 px-3 py-1.5 rounded-lg font-medium">
            {total} total entries
          </span>
          <button
            onClick={loadLogs}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#1565C0] bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
              <input
                type="text"
                placeholder="Search actions..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]"
              />
            </div>
          </form>

          {/* Entity Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
            <select
              value={entityType}
              onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
              className="pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0] min-w-[160px]"
            >
              <option value="all">All Types</option>
              <option value="user">User</option>
              <option value="application">Application</option>
              <option value="disbursement">Disbursement</option>
              <option value="agent">Agent</option>
              <option value="settings">Settings</option>
            </select>
          </div>

          {/* Date From */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]"
              placeholder="From"
            />
          </div>

          {/* Date To */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] pointer-events-none" />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1565C0]/20 focus:border-[#1565C0]"
              placeholder="To"
            />
          </div>

          {/* Clear Filters */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2.5 text-xs font-semibold text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </div>

        {/* Active Filters Display */}
        {hasFilters && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
            {entityType !== "all" && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
                <Filter className="h-3 w-3" /> {entityType}
              </span>
            )}
            {search && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-violet-50 text-violet-700 rounded-lg text-xs font-medium">
                <Search className="h-3 w-3" /> "{search}"
              </span>
            )}
            {dateFrom && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium">
                <Calendar className="h-3 w-3" /> From: {dateFrom}
              </span>
            )}
            {dateTo && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">
                <Calendar className="h-3 w-3" /> To: {dateTo}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-3 border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" style={{ borderWidth: "3px" }} />
              <p className="text-sm text-[#6B7280]">Loading logs...</p>
            </div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF]">
            <ScrollText className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">No audit logs found</p>
            <p className="text-xs mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-[#6B7280]">Timestamp</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-[#6B7280]">Admin</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-[#6B7280]">Action</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-[#6B7280]">Entity</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-[#6B7280]">Entity ID</th>
                    <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider font-bold text-[#6B7280]">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map((log) => {
                    const config = ENTITY_CONFIG[log.entity_type] || { label: log.entity_type || "Unknown", color: "bg-gray-100 text-gray-700", icon: Info };
                    const EntityIcon = config.icon;
                    const isExpanded = expandedId === log.id;
                    let details = null;
                    try {
                      details = log.details ? (typeof log.details === "string" ? JSON.parse(log.details) : log.details) : null;
                    } catch { details = null; }

                    return (
                      <tr key={log.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-[#9CA3AF] flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-[#0A2540]">{formatDate(log.created_at)}</p>
                              <p className="text-[10px] text-[#9CA3AF]">{timeAgo(log.created_at)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1565C0] to-[#0D47A1] flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                              {(log.admin_name || "S").charAt(0).toUpperCase()}
                            </div>
                            <span className="text-xs font-medium text-[#0A2540] truncate max-w-[100px]">{log.admin_name || "System"}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-xs text-[#374151] font-medium leading-relaxed">{log.action}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${config.color}`}>
                            <EntityIcon className="h-3 w-3" />
                            {config.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-[10px] font-mono text-[#6B7280] bg-gray-50 px-2 py-1 rounded">
                            {log.entity_id ? log.entity_id.substring(0, 8) + "..." : "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          {details ? (
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : log.id)}
                              className="flex items-center gap-1 text-[10px] font-semibold text-[#1565C0] hover:text-[#0D47A1] transition-colors"
                            >
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              {isExpanded ? "Hide" : "View"}
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#D1D5DB]">—</span>
                          )}
                          {isExpanded && details && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-lg text-[10px] font-mono text-[#4B5563] max-w-xs break-all">
                              {Object.entries(details).map(([k, v]) => (
                                <div key={k}><span className="font-bold text-[#374151]">{k}:</span> {String(v)}</div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-50">
              {logs.map((log) => {
                const config = ENTITY_CONFIG[log.entity_type] || { label: log.entity_type || "Unknown", color: "bg-gray-100 text-gray-700", icon: Info };
                const EntityIcon = config.icon;
                const isExpanded = expandedId === log.id;
                let details = null;
                try {
                  details = log.details ? (typeof log.details === "string" ? JSON.parse(log.details) : log.details) : null;
                } catch { details = null; }

                return (
                  <div key={log.id} className="p-4 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${config.color}`}>
                        <EntityIcon className="h-3 w-3" />
                        {config.label}
                      </span>
                      <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">{timeAgo(log.created_at)}</span>
                    </div>
                    <p className="text-sm text-[#374151] font-medium mb-1.5">{log.action}</p>
                    <div className="flex items-center gap-3 text-[10px] text-[#9CA3AF]">
                      <span className="flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        {log.admin_name || "System"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                    {log.entity_id && (
                      <p className="text-[10px] font-mono text-[#9CA3AF] mt-1">ID: {log.entity_id.substring(0, 12)}...</p>
                    )}
                    {details && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                        className="mt-2 flex items-center gap-1 text-[10px] font-semibold text-[#1565C0]"
                      >
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {isExpanded ? "Hide details" : "Show details"}
                      </button>
                    )}
                    {isExpanded && details && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-lg text-[10px] font-mono text-[#4B5563]">
                        {Object.entries(details).map(([k, v]) => (
                          <div key={k}><span className="font-bold">{k}:</span> {String(v)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-[#6B7280]">
              Page {page} of {totalPages} ({total} entries)
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4 text-[#6B7280]" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                      page === pageNum
                        ? "bg-[#1565C0] text-white"
                        : "border border-gray-200 text-[#6B7280] hover:bg-white"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-[#6B7280]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
